/**
 * Turns a schema `Media` reference into a URL.
 *
 * Two homes for media:
 *
 *  1. Shared libraries, served as static files by Firebase Hosting (same
 *     origin as the player), staged by tools/src/stage-libraries.mjs:
 *       /libraries/images/<path>.<ext>       (Resources/Libraries/Images)
 *       /libraries/sounds/<path>.<ext>       (Resources/Libraries/Sounds)
 *       /libraries/audio/<name>.<ext>        (Resources/Audio, background music)
 *       /libraries/templates/<t>/<asset>.png (Resources/Templates)
 *       /libraries/manifest.json             { images: { "<path>": "png" }, … }
 *     Library files are referenced without extension (Unity's Resources.Load
 *     convention); the manifest gives the real extension. Until it is loaded
 *     we assume png / mp3.
 *
 *  2. Per-project media (personalized images, recorded sounds), uploaded by the
 *     importProject Cloud Function to Firebase Storage (public-read rules):
 *       projects/<projectId>/media/<file>
 */
import type { Media } from "@makeit/schema";
import { firebaseConfig } from "../firebase";

/** Base URL of the libraries. Override with VITE_LIBRARY_BASE (e.g. a CDN). */
const LIBRARY_BASE: string = ((import.meta.env.VITE_LIBRARY_BASE as string | undefined) ?? "/libraries").replace(/\/$/, "");
const STORAGE_HOST = import.meta.env.VITE_EMULATORS === "1" ? "http://127.0.0.1:9199" : "https://firebasestorage.googleapis.com";
const STORAGE_BASE = `${STORAGE_HOST}/v0/b/${firebaseConfig.storageBucket}/o`;

export type MediaKind = "image" | "sound";

interface Manifest {
  images: Record<string, string>;
  sounds: Record<string, string>;
  audio?: Record<string, string>;
  templates?: Record<string, string>;
}
let manifest: Manifest | null = null;
let manifestPromise: Promise<void> | null = null;

/** Loads the library manifest once (best effort; failures fall back to defaults). */
export function ensureLibraryManifest(): Promise<void> {
  if (!manifestPromise) {
    manifestPromise = fetch(`${LIBRARY_BASE}/manifest.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (m && typeof m === "object") manifest = m as Manifest;
      })
      .catch(() => undefined);
  }
  return manifestPromise;
}

export function libraryUrl(path: string): string {
  return `${LIBRARY_BASE}/${path.split("/").map(encodeURIComponent).join("/")}`;
}

/** Firebase Storage REST URL for an object (works with public-read rules). */
export function storageUrl(path: string): string {
  return `${STORAGE_BASE}/${encodeURIComponent(path)}?alt=media`;
}

export function mediaUrl(media: Media | null | undefined, kind: MediaKind, projectId: string): string | null {
  if (!media || !media.fileName) return null;
  switch (media.source) {
    case "library": {
      const table = kind === "image" ? manifest?.images : manifest?.sounds;
      const ext = table?.[media.fileName] ?? (kind === "image" ? "png" : "mp3");
      return libraryUrl(`${kind === "image" ? "images" : "sounds"}/${media.fileName}.${ext}`);
    }
    case "personalized":
    case "recorded":
      return storageUrl(`projects/${projectId}/media/${media.fileName}`);
    default:
      return null;
  }
}

/** Background music (Resources/Audio/bg_*). */
export function backgroundMusicUrl(name: string): string | null {
  if (!name || name === "none") return null;
  const ext = manifest?.audio?.[name] ?? "mp3";
  return libraryUrl(`audio/${name}.${ext}`);
}

/** Template assets (Resources/Templates/<template>/{background,panel,home}.png). */
export function templateUrl(template: string, asset: "background" | "panel" | "home"): string {
  const name = template === "none" || template.startsWith("#") ? "default" : template;
  const ext = manifest?.templates?.[`${name}/${asset}`] ?? "png";
  return libraryUrl(`templates/${name}/${asset}.${ext}`);
}
